/**
 * ExplainRadarModal — One-time onboarding + re-invocable dimension explainer
 *
 * Auto-fires on first visit to MyImpact. Dismissible. Re-invocable via the
 * "?" icon in the Impact Signature header.
 *
 * Follows CLAUDE.md conventions:
 *   - useAppTranslation (pages namespace)
 *   - CSS custom property tokens only
 *   - CSS logical properties for RTL
 *
 * Jira: DEV-678
 */

import React, { useEffect, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { DIMENSION_CONFIG } from '../DimensionRadar/dimensionConfig';
import type { DimKey } from '../DimensionRadar/dimensionConfig';
import ImpactSignatureMini from '../DimensionRadar/ImpactSignatureMini';
import type { TierLevel } from '../DimensionRadar/ImpactSignatureMini';
import styles from './ExplainRadarModal.module.css';

/* ── Dimension display order (matches radar layout) ── */
const DIM_ORDER: DimKey[] = ['mind', 'voice', 'heart', 'body', 'wings', 'roots'];

/* ── Dimension names & descriptors (same as DimensionRadar.tsx) ── */
const DIM_META: Record<DimKey, { name: string; descriptor: string }> = {
  mind:  { name: 'Mind',  descriptor: 'Understanding & Insight' },
  voice: { name: 'Voice', descriptor: 'Awareness & Influence' },
  heart: { name: 'Heart', descriptor: 'Connection & Compassion' },
  body:  { name: 'Body',  descriptor: 'Action & Execution' },
  wings: { name: 'Wings', descriptor: 'Support & Enablement' },
  roots: { name: 'Roots', descriptor: 'Commitment & Steadfastness' },
};

/* ── i18n key mapping per dimension ── */
const DIM_I18N_KEYS: Record<DimKey, string> = {
  mind:  'impact.radarModalDimMind',
  voice: 'impact.radarModalDimVoice',
  heart: 'impact.radarModalDimHeart',
  body:  'impact.radarModalDimBody',
  wings: 'impact.radarModalDimWings',
  roots: 'impact.radarModalDimRoots',
};

/* ── Sample tiers for the illustrative radar ── */
const SAMPLE_TIERS: Record<string, TierLevel> = {
  mind:  'gold',
  voice: 'silver',
  heart: 'gold',
  body:  'bronze',
  wings: 'silver',
  roots: 'bronze',
};

/* ── Belt key BASE for first-visit tracking (chunk B slice D, §7.16):
   the trigger of record is the SERVER `radar_explained` milestone; the
   host keeps a USER-namespaced localStorage belt (`${KEY}:{GUID}`) only
   to bridge the refetch gap. The old un-namespaced global key was a
   DEV-1443-class shared-browser bug (user A suppressed user B's reveal). */
export const RADAR_EXPLAIN_SEEN_KEY = 'l4e_radar_explain_seen';

interface ExplainRadarModalProps {
  onClose: () => void;
}

export default function ExplainRadarModal({ onClose }: ExplainRadarModalProps) {
  const { t } = useAppTranslation('pages');

  /* ── Escape key handler ── */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /* ── Dismiss — purely presentational; the HOST records the server
     `radar_explained` milestone + its user-scoped belt on close. ── */
  const handleDismiss = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={handleDismiss}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <div className={styles.header}>
          <h2 className={styles.title}>{t('impact.radarModalTitle')}</h2>
          <button
            className={styles.closeBtn}
            onClick={handleDismiss}
            aria-label={t('impact.radarModalGotIt')}
          >
            ✕
          </button>
        </div>

        {/* ── Body ── */}
        <div className={styles.body}>
          <p className={styles.intro}>{t('impact.radarModalIntro')}</p>

          {/* Sample radar illustration */}
          <div className={styles.sampleRadar}>
            <ImpactSignatureMini tiers={SAMPLE_TIERS} />
          </div>

          {/* Dimension cards */}
          <div className={styles.dimList}>
            {DIM_ORDER.map((dimKey) => {
              const cfg = DIMENSION_CONFIG[dimKey];
              const meta = DIM_META[dimKey];
              const Icon = cfg.icon;
              return (
                <div key={dimKey} className={styles.dimRow}>
                  <span className={styles.dimIcon}>
                    <Icon color={cfg.color} size={20} strokeWidth={2} />
                  </span>
                  <div className={styles.dimText}>
                    <span className={styles.dimName}>
                      {meta.name} — {meta.descriptor}
                    </span>
                    <span className={styles.dimDesc}>
                      {t(DIM_I18N_KEYS[dimKey])}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tier explanation */}
          <p className={styles.tierSection}>{t('impact.radarModalTiers')}</p>
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <button className={styles.gotItBtn} onClick={handleDismiss}>
            {t('impact.radarModalGotIt')}
          </button>
        </div>
      </div>
    </div>
  );
}
